import { useState, useEffect } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useTranslation } from "react-i18next"
import { ArrowLeft } from "lucide-react"
import { useDeal, useCreateDeal, useUpdateDeal } from "@/hooks/use-deals"
import { cn } from "@/lib/utils"
import { useDealTypes } from "@/hooks/use-deal-types"
import { useCompanies } from "@/hooks/use-companies"
import { ApiClientError } from "@/lib/api-client"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"

const dealSchema = z.object({
  name: z.string().min(1, "Nazwa jest wymagana"),
  deal_type_id: z.string().min(1, "Pipeline jest wymagany"),
  phase_id: z.string().min(1, "Etap jest wymagany"),
  company_id: z.string().optional(),
  value: z.string().optional(),
  currency: z.string().optional(),
  probability: z.string().optional(),
  expected_close_date: z.string().optional(),
  owner_id: z.string().optional(),
  description: z.string().optional(),
})

type DealForm = z.infer<typeof dealSchema>

export function DealFormPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const isEdit = !!id
  const [error, setError] = useState<string | null>(null)

  const { data: dealData } = useDeal(id)
  const { data: dealTypesData } = useDealTypes()
  const { data: companiesData } = useCompanies()
  const createMutation = useCreateDeal()
  const updateMutation = useUpdateDeal()

  const dealTypes = dealTypesData?.data ?? []
  const companies = companiesData?.data ?? []

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<DealForm>({
    resolver: zodResolver(dealSchema),
    values: isEdit && dealData ? {
      name: dealData.data.name,
      deal_type_id: dealData.data.deal_type.id,
      phase_id: dealData.data.phase.id,
      company_id: dealData.data.company?.id || "",
      value: dealData.data.value != null ? String(dealData.data.value) : "",
      currency: dealData.data.currency,
      probability: dealData.data.probability != null ? String(dealData.data.probability) : "",
      expected_close_date: dealData.data.expected_close_date || "",
      owner_id: dealData.data.owner?.id || "",
      description: dealData.data.description || "",
    } : {
      name: "",
      deal_type_id: dealTypes[0]?.id || "",
      phase_id: "",
      company_id: "",
      value: "",
      currency: "PLN",
      probability: "",
      expected_close_date: "",
      owner_id: "",
      description: "",
    },
  })

  const selectedDealTypeId = watch("deal_type_id")
  const selectedDealType = dealTypes.find((dt) => dt.id === selectedDealTypeId)
  const availablePhases = selectedDealType?.phases.filter(p => !p.is_won && !p.is_lost) ?? []

  // Auto-select first phase when deal type changes
  useEffect(() => {
    if (!isEdit && availablePhases.length > 0 && !watch("phase_id")) {
      setValue("phase_id", availablePhases[0].id)
    }
  }, [selectedDealTypeId, availablePhases, isEdit, setValue, watch])

  const onSubmit = async (data: DealForm) => {
    try {
      setError(null)
      const payload: Record<string, unknown> = {
        ...data,
        value: data.value ? parseFloat(data.value) : null,
        probability: data.probability ? parseInt(data.probability) : null,
        company_id: data.company_id || null,
        owner_id: data.owner_id || null,
        expected_close_date: data.expected_close_date || null,
      }

      if (isEdit) {
        await updateMutation.mutateAsync({ id, data: payload })
        toast.success("Szansa zaktualizowana")
      } else {
        await createMutation.mutateAsync(payload)
        toast.success("Szansa utworzona")
      }
      navigate("/deals")
    } catch (err) {
      if (err instanceof ApiClientError) setError(err.message)
      else setError("Wystąpił nieoczekiwany błąd")
    }
  }

  const inputClass = "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"

  return (
    <div className="mx-auto max-w-[720px] space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted/50">
          <ArrowLeft size={18} />
        </button>
        <h1 className="text-xl font-semibold text-foreground">
          {isEdit ? "Edytuj szansę" : t("deals.new")}
        </h1>
      </div>

      {error && (
        <div className="rounded-lg px-4 py-3 text-sm bg-error-light text-error-dark">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic info */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dane szansy</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Nazwa *</label>
              <input {...register("name")} className={cn(inputClass, errors.name && "border-error")}
                placeholder="np. Wdrożenie systemu CRM" />
              {errors.name && <p className="text-xs text-error">{errors.name.message}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Pipeline *</label>
              <select {...register("deal_type_id")} className={inputClass}>
                <option value="">— Wybierz —</option>
                {dealTypes.map((dt) => (
                  <option key={dt.id} value={dt.id}>{dt.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Etap *</label>
              <select {...register("phase_id")} className={cn(inputClass, errors.phase_id && "border-error")}>
                <option value="">— Wybierz —</option>
                {availablePhases.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.probability}%)</option>
                ))}
              </select>
              {errors.phase_id && <p className="text-xs text-error">{errors.phase_id.message}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Kontrahent</label>
              <select {...register("company_id")} className={inputClass}>
                <option value="">— Brak —</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Planowane zamknięcie</label>
              <input {...register("expected_close_date")} type="date" className={inputClass} />
            </div>
          </div>
        </section>

        {/* Value */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Wartość</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Wartość</label>
              <input {...register("value")} type="number" step="0.01" min="0" className={inputClass} />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Waluta</label>
              <select {...register("currency")} className={inputClass}>
                <option value="PLN">PLN</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
                <option value="GBP">GBP</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Prawdopodobieństwo (%)</label>
              <input {...register("probability")} type="number" min="0" max="100" className={inputClass} />
            </div>
          </div>
        </section>

        {/* Description */}
        <section className="space-y-4">
          <h2 className="text-sm font-semibold uppercase text-muted-foreground">Dodatkowe</h2>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Opis</label>
            <textarea {...register("description")} rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none" />
          </div>
        </section>

        {/* Actions */}
        <div className="flex justify-end gap-3 border-t border-border pt-4">
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? t("common.loading") : t("common.save")}
          </Button>
        </div>
      </form>
    </div>
  )
}
